import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, Coins, Home, Trophy, Wallet as WalletIcon, Sparkles, ShieldCheck, Banknote, Users } from "lucide-react";
import MusicToggle from "@/components/MusicToggle";
import SfxToggle from "@/components/SfxToggle";

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/"); };

  const links = [
    { to: "/lobby", label: "Lobby", icon: Home, tid: "nav-lobby" },
    { to: "/daily", label: "Daily", icon: Sparkles, tid: "nav-daily" },
    { to: "/leaderboard", label: "Leaders", icon: Trophy, tid: "nav-leaderboard" },
    { to: "/wallet", label: "Wallet", icon: WalletIcon, tid: "nav-wallet" },
    { to: "/referrals", label: "Invite", icon: Users, tid: "nav-referrals" },
  ];
  if (user?.role === "admin") {
    links.push({ to: "/admin/revenue", label: "Revenue", icon: ShieldCheck, tid: "nav-revenue" });
    links.push({ to: "/admin/withdrawals", label: "Payouts", icon: Banknote, tid: "nav-admin-wd" });
  }

  return (
    <div className="vexis-ambient min-h-screen pb-24 md:pb-8">
      <header className="sticky-nav">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <Link to="/lobby" className="flex items-center gap-2" data-testid="brand-home">
            <span className="chip-icon text-[22px]" />
            <span className="font-display text-xl tracking-tight">VEXIS</span>
            <span className="pill pill-gold hidden sm:inline-flex ml-2">Skill Casino</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={l.tid}
                className={({ isActive }) => `px-3 py-2 rounded-lg text-sm transition ${isActive ? "text-[color:var(--gold)] bg-white/5" : "text-slate-400 hover:text-slate-100"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <MusicToggle />
            <SfxToggle />
            <div className="pill pill-gold" data-testid="chip-balance">
              <Coins className="w-3.5 h-3.5" /> {user?.chips?.toLocaleString?.() ?? 0}
            </div>
            <button onClick={doLogout} className="btn-ghost hidden sm:inline-flex items-center gap-2 text-sm" data-testid="logout-button">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 sm:py-10">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 backdrop-blur-lg bg-[rgba(9,11,14,0.85)] border-t border-white/10">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${links.length + 1}, minmax(0, 1fr))` }}>
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`mnav-${l.tid}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] ${isActive ? "text-[color:var(--gold)]" : "text-slate-500"}`}>
              <l.icon className="w-5 h-5" /> {l.label}
            </NavLink>
          ))}
          <button onClick={doLogout} className="flex flex-col items-center gap-1 py-2.5 text-[11px] text-slate-500" data-testid="mnav-logout">
            <LogOut className="w-5 h-5" /> Logout
          </button>
        </div>
      </nav>
    </div>
  );
}
